import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from '../app/features/auth/services/auth.service';
import { environment } from '../environments/environment';

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface LeaveRequest {
  id: number;
  workerId: number;
  workerFirstName: string;
  workerLastName: string;
  realEstateId: number;
  realEstateName: string;
  startDate: number[];
  endDate: number[];
  requestedDays: number;
  reason: string;
  status: LeaveStatus;
  managerComment: string | null;
  reviewedByName: string | null;
  reviewedAt: number[] | null;
  createdAt: number[];
}

export interface ReviewPayload {
  decision: LeaveStatus;
  managerComment: string;
}

export interface LeavesResponse {
  content: LeaveRequest[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

export interface WorkerLeaveSummary {
  workerId: number;
  workerFirstName: string;
  workerLastName: string;
  totalAccruedDays: number;
  usedDays: number;
  pendingDays: number;
  remainingDays: number;
  history: LeaveRequest[];
}

@Injectable({
  providedIn: 'root'
})
export class LeaveService {

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${this.authService.getToken()}`,
      'Content-Type': 'application/json'
    });
  }

  private url(key: keyof typeof environment.endpoints.leaves, params: Record<string, string> = {}): string {
    let u: string = environment.endpoints.leaves[key];
    Object.entries(params).forEach(([k, v]) => u = u.replace(`{${k}}`, v));
    return u;
  }

  /** GET /api/v1/real-estates/{realEstateId}/leaves/requests */
  getByProperty(realEstateId: number, page = 0, size = 10, status?: LeaveStatus): Observable<LeavesResponse> {
    const url = this.url('byProperty', { realEstateId: String(realEstateId) });
    let params = new HttpParams().set('page', page).set('size', size);
    if (status) params = params.set('status', status);
    return this.http.get<LeavesResponse>(url, { headers: this.getHeaders(), params });
  }

  /** PATCH /api/v1/real-estates/{realEstateId}/leaves/requests/{requestId}/review */
  review(realEstateId: number, requestId: number, payload: ReviewPayload): Observable<LeaveRequest> {
    const url = this.url('review', {
      realEstateId: String(realEstateId),
      requestId: String(requestId)
    });
    return this.http.patch<LeaveRequest>(url, payload, { headers: this.getHeaders() });
  }

  /** GET /api/v1/real-estates/{realEstateId}/leaves/workers/{workerId}/summary */
  getWorkerSummary(realEstateId: number, workerId: number): Observable<WorkerLeaveSummary> {
    const url = this.url('workerSummary', {
      realEstateId: String(realEstateId),
      workerId: String(workerId)
    });
    return this.http.get<WorkerLeaveSummary>(url, { headers: this.getHeaders() });
  }

  // Formate un tableau de dates Spring Boot [year, month, day, ...] en string lisible
  formatDateArray(arr: number[]): string {
    if (!arr || arr.length < 3) return '-';
    const d = new Date(arr[0], arr[1] - 1, arr[2]);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }
}
